// The cove caves overlay (/api/cove-caves): cave lines, entrances, springs and survey stations from the club's
// GeoPackage, for caves the atlas has no survey of.  Heights are NAVD88 as given: everything sits in `world`, not
// `cave`, so the vertical datum shift doesn't move it.  Mixed into Viewer (see the end of viewer.js).
import * as THREE from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';

// how each line's depth was got (z_source in the file) -> what it looks like (also used for the legend)
export const COVE_STYLE = {
  survey: { color: '#ffb347', w: 3, op: 1, label: 'Surveyed, pinned to the LIDAR at the entrances' },
  bands: { color: '#ffb347', w: 2.5, op: 0.85, dash: [8, 5], label: 'Depth from the 100 ft elevation bands (approx.)' },
  unknown: { color: '#c9c4b8', w: 2, op: 0.6, dash: [3, 4], label: 'Depth unknown (drawn 30 m below the ground)' },
};
const ENTRANCE_COLOR = '#ffb347', SPRING_COLOR = '#5fb8ff';

function pointTex(draw) {
  const cv = document.createElement('canvas'); cv.width = cv.height = 48; draw(cv.getContext('2d'));
  return new THREE.CanvasTexture(cv);
}

export const CoveMixin = {
  coveMat(key, o) {
    this.cMats ||= {};
    if (!this.cMats[key]) {
      const m = new LineMaterial({ transparent: true, depthTest: false, ...o });
      m.resolution.set(innerWidth, innerHeight); this.lineMats.push(m); this.cMats[key] = m;
    }
    return this.cMats[key];
  },

  setCove(doc) {
    this.coveDoc = doc;
    if (this.coveObj) { this.world.remove(this.coveObj); this.coveObj.traverse(o => o.geometry?.dispose()); this.coveObj = null; }
    if (!doc || !this.origin) { this.labels.setCove([]); return; }
    const [xc, yc, zc] = this.origin, L = (e, n, z) => [e - xc, (z ?? this.groundAt(e, n)) - zc, -(n - yc)];
    const grp = this.coveObj = new THREE.Group(); this.world.add(grp);
    grp.userData.parts = { coveLines: [], coveEntrances: [], coveSprings: [], coveStations: [] };

    // lines, one object per depth source
    const segs = {};
    for (const l of doc.lines || []) {
      const a = (segs[l.kind in COVE_STYLE ? l.kind : 'unknown'] ||= []);
      for (let i = 1; i < l.pts.length; i++) a.push(...L(...l.pts[i - 1]), ...L(...l.pts[i]));
    }
    for (const [k, arr] of Object.entries(segs)) {
      const st = COVE_STYLE[k], geo = new LineSegmentsGeometry(); geo.setPositions(arr);
      const line = new LineSegments2(geo, this.coveMat(k, { color: st.color, linewidth: st.w, opacity: st.op,
        dashed: !!st.dash, dashSize: st.dash?.[0] ?? 1, gapSize: st.dash?.[1] ?? 1 }));
      line.computeLineDistances(); line.renderOrder = 38; grp.add(line); grp.userData.parts.coveLines.push(line);
    }

    // entrances, springs and stations as screen-sized points
    this.coveTex ||= {
      entrance: pointTex(x => { x.beginPath(); x.moveTo(24, 6); x.lineTo(42, 40); x.lineTo(6, 40); x.closePath(); x.fillStyle = ENTRANCE_COLOR; x.fill(); x.lineWidth = 5; x.strokeStyle = '#141413'; x.stroke(); }),
      spring: pointTex(x => { x.beginPath(); x.arc(24, 24, 15, 0, Math.PI * 2); x.fillStyle = SPRING_COLOR; x.fill(); x.lineWidth = 6; x.strokeStyle = '#141413'; x.stroke(); }),
    };
    const points = (list, part, size, tex, color) => {
      if (!list.length) return;
      const pos = new Float32Array(3 * list.length);
      list.forEach((p, i) => { const v = L(p.e, p.n, p.z); v[1] += 1; pos.set(v, 3 * i); });
      const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      const m = new THREE.PointsMaterial({ size, sizeAttenuation: false, map: tex || null, color: tex ? 0xffffff : color,
        transparent: true, alphaTest: tex ? 0.3 : 0, depthTest: false });
      const o = new THREE.Points(g, m); o.renderOrder = 39; grp.add(o); grp.userData.parts[part].push(o);
    };
    const E = doc.entrances || [];
    points(E.filter(e => e.type !== 'spring'), 'coveEntrances', 18, this.coveTex.entrance);
    points(E.filter(e => e.type === 'spring'), 'coveSprings', 18, this.coveTex.spring);
    points(doc.stations || [], 'coveStations', 4, null, ENTRANCE_COLOR);
    this.clipObject(grp);

    // labels: each cave over the vertex nearest its middle, plus entrances and springs
    const labels = [], byCave = new Map();
    for (const l of doc.lines || []) { const c = byCave.get(l.cave) || byCave.set(l.cave, { pts: [], kinds: new Set() }).get(l.cave); c.pts.push(...l.pts); c.kinds.add(l.kind); }
    for (const [name, c] of byCave) {
      const me = c.pts.reduce((s, p) => s + p[0], 0) / c.pts.length, mn = c.pts.reduce((s, p) => s + p[1], 0) / c.pts.length;
      const p = c.pts.reduce((b, p) => ((p[0] - me) ** 2 + (p[1] - mn) ** 2 < (b[0] - me) ** 2 + (b[1] - mn) ** 2 ? p : b));
      const tag = c.kinds.size === 1 && c.kinds.has('unknown') ? 'depth unknown' : c.kinds.has('unknown') ? 'approx.; part depth unknown' : 'approx.';
      labels.push({ text: `${name} (${tag})`, kind: 'covecave', layer: 'coveLines', x: p[0] - xc, z: -(p[1] - yc) });
    }
    for (const e of E) labels.push({ text: e.name, kind: e.type === 'spring' ? 'spring' : 'feature', layer: e.type === 'spring' ? 'coveSprings' : 'coveEntrances',
      x: e.e - xc, y: (e.z ?? this.groundAt(e.e, e.n)) - zc, z: -(e.n - yc) });
    this.labels.setCove(labels);
    this.applyCove();
  },

  applyCove() {
    const parts = this.coveObj?.userData.parts; if (!parts) return;
    for (const [k, objs] of Object.entries(parts)) for (const o of objs) o.visible = !!this.opts[k];
  },
};
